import {useFieldArray, useFormContext} from 'react-hook-form';
import {useState} from 'react';
import {SVG} from '../../../../../../constants/assetLists';
import AppButton from '../../../../../../components/common/AppButton';
import {
  createDefaultSubBranch,
  NetworkDetailsFormValues,
  SelectedTreeNode,
} from '../../types';
import {
  canDeleteNodeWithoutContentGuard,
  getShowRemoveButton,
} from '../../hooks/useNetworkTreeGuards';
import SubBranchTreeRow from './SubBranchTreeRow';
import TreeDeleteConfirmationDialog from './TreeDeleteConfirmationDialog';
import {useSnackbar} from '../../../../../../context/SnackbarContextProvider';
import {canDeleteNode} from '../networkReferenceUtils';
import { nodeSetupCompleteSchema } from '../../networkDetailsSchema';
import {isAdmin} from '../../../../../../utils';

interface BranchTreeRowProps {
  networkIndex: number;
  branchIndex: number;
  totalNetworkCount: number;
  networkBranchCount: number;
  expandedSet: Set<string>;
  setExpandedSet: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleExpand: (id: string) => void;
  onRemove: () => void;
  selectedNode: SelectedTreeNode | null;
  onSelectNode: (node: SelectedTreeNode) => void;
}

function BranchTreeRow({
  networkIndex,
  branchIndex,
  totalNetworkCount,
  networkBranchCount,
  expandedSet,
  setExpandedSet,
  toggleExpand,
  onRemove,
  selectedNode,
  onSelectNode,
}: BranchTreeRowProps) {
  const {showSnackbar} = useSnackbar();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const {control, watch} = useFormContext<NetworkDetailsFormValues>();

  const {append: appendSubBranch, remove: removeSubBranch} = useFieldArray({
    control,
    name: `network.${networkIndex}.branches.${branchIndex}.subBranches`,
    keyName: 'id',
  });

  const node = watch(`network.${networkIndex}.branches.${branchIndex}`);
  const subBranches = node?.subBranches ?? [];

  if (!node) return null;

  const validationResult = nodeSetupCompleteSchema.safeParse(node);
  const isNodeValid = validationResult.success;

  const depth = 1;
  const paddingInline = Math.min(12 + depth * 8, 56);
  const hasSubChildren = subBranches.length > 0;
  const expanded = expandedSet.has(node.id);
  const caretIcon = expanded ? SVG.expandIcon : SVG.lessThanIcon;

  const showRemoveButton = getShowRemoveButton({
    isNetwork: false,
    isBranch: true,
    totalNetworkCount,
    networkBranchCount,
  });

  const handleRemoveNode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  const handleConfirmRemove = () => {
    if (isAdmin() || canDeleteNodeWithoutContentGuard(node.id)) {
      onRemove();
      setShowDeleteDialog(false);
      return;
    }

    const result = canDeleteNode(node);

    if (!result.allowed) {
      showSnackbar({
        message: result.message,
        type: 'error',
        duration: 5000,
      });
      setShowDeleteDialog(false);
      return;
    }

    onRemove();
    setShowDeleteDialog(false);
  };

  const addSubBranch = (e: React.MouseEvent) => {
    e.stopPropagation();
    appendSubBranch(createDefaultSubBranch());

    onSelectNode({
      level: 'subBranch',
      networkIndex,
      branchIndex,
      subBranchIndex: subBranches?.length,
    });
    setExpandedSet(prev => {
      const id = node.id;
      const next = new Set(prev);
      if (next.has(id)) return next;
      else next.add(id);
      return next;
    });
  };

  const isSelected =
    selectedNode?.level === 'branch' &&
    selectedNode.networkIndex === networkIndex &&
    selectedNode.branchIndex === branchIndex;

  return (
    <div>
      <TreeDeleteConfirmationDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmRemove}
      />
      <div
        role="button"
        tabIndex={0}
        className={`w-full flex justify-between items-center gap-2 py-2 rounded-lg transition-colors duration-200 hover:bg-pastelBlue group cursor-pointer ${
          isSelected ? 'bg-pastelBlue' : ''
        }`}
        style={{
          paddingInlineStart: `${paddingInline}px`,
          paddingInlineEnd: '8px',
        }}
        onClick={() =>
          onSelectNode({level: 'branch', networkIndex, branchIndex})
        }
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            onSelectNode({level: 'branch', networkIndex, branchIndex});
          }
        }}>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            {hasSubChildren && (
              <button
                type="button"
                className="w-[12px] h-[12px]"
                onClick={e => {
                  e.stopPropagation();
                  toggleExpand(node.id);
                }}>
                <img
                  src={caretIcon}
                  alt="caret"
                  className="w-full h-full object-contain"
                />
              </button>
            )}
            <div className="w-[20px] h-[20px]">
              <img
                src={SVG.clientIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span
              className={`${!isNodeValid ? 'text-raspberryRed' : 'text-black'}`}>
              {node?.name || 'שם סניף'}
            </span>
            {!isNodeValid && (
              <span
                className="w-[14px] h-[14px] rounded-full bg-raspberryRed shrink-0"
                aria-label="שדה חובה חסר"
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <AppButton
            iconStart={
              <img src={SVG.addIcon} className="w-3 h-3 object-contain" />
            }
            onClick={addSubBranch}>
            תת סניף
          </AppButton>

          {showRemoveButton && (
            <button
              type="button"
              className="w-3 h-3 hidden group-hover:block"
              onClick={handleRemoveNode}>
              <img
                src={SVG.closeDarkIcon}
                className="w-full h-full object-contain"
                alt="remove"
              />
            </button>
          )}
        </div>
      </div>

      {hasSubChildren && expanded && (
        <div className="mt-1 flex flex-col gap-1">
          {subBranches.map((subBranch, subBranchIndex) => (
            <SubBranchTreeRow
              key={subBranch.id}
              networkIndex={networkIndex}
              branchIndex={branchIndex}
              subBranchIndex={subBranchIndex}
              totalNetworkCount={totalNetworkCount}
              networkBranchCount={networkBranchCount}
              onRemove={() => removeSubBranch(subBranchIndex)}
              selectedNode={selectedNode}
              onSelectNode={onSelectNode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default BranchTreeRow;
